import { Divider, Form, FormInstance, Input, Row, Select } from "antd";
import {
  CatalogActivityTypes,
  CatalogInstallationKeys,
  DistributionPointRequestResource,
} from "api/generated";
import tooltipList from "../tooltip.json";
import { isValidRfc } from "../../../../services/utils/rfcValidator";
import { useMemo } from "react";
import Validations from "./DistributionPointValidations";
import FormCol from "../../../../common/components/form/ResponsiveCol";
import { timeZones } from "services/utils/StaticInfoEnums";
import { FormItem } from "common/components/form/FormItem";
interface GeneralDataFormProps {
  formData: FormInstance<DistributionPointRequestResource>;
  catalogActivityTypes: CatalogActivityTypes[];
  installationKeys: CatalogInstallationKeys[];
}

function GeneralDataForm({
  formData,
  catalogActivityTypes,
  installationKeys,
}: GeneralDataFormProps) {
  const rfc = Form.useWatch("rfc", formData);
  const installationKey = Form.useWatch("installationKey", formData);
  const isRFCMoral = useMemo(() => {
    return !!rfc && isValidRfc(rfc) && rfc.length === 12;
  }, [rfc]);

  return (
    <div>
      <Divider
        orientation="left"
        style={{ fontSize: "15px", color: "#606676" }}
      >
        <div>Datos del punto de distribución</div>
      </Divider>
      <Row gutter={24}>
        <FormCol>
          <FormItem<DistributionPointRequestResource>
            label="RFC del contribuyente"
            name="rfc"
            rules={Validations.RFCRules}
            normalize={(value) => (value || "").toUpperCase()}
            validateTrigger={["onChange", "onBlur"]}
          >
            <Input
              className="common-input"
              placeholder="RFC del contribuyente"
              addonAfter={Validations.ExtraInfoBtn(
                "Introduce el RFC del contribuyente",
                [tooltipList.RFC]
              )}
              autoComplete="off"
            />
          </FormItem>
        </FormCol>
        <FormCol>
          <FormItem<DistributionPointRequestResource>
            label="Nombre o razón social de la empresa"
            rules={Validations.NameRules}
            name="name"
            normalize={(value) => (value || "").toUpperCase()}
          >
            <Input
              className="common-input"
              placeholder="Nombre o razón social de la empresa"
              autoComplete="off"
              addonAfter={Validations.ExtraInfoBtn(
                "Introduce el nombre o razón social del contribuyente",
                [tooltipList.razonSocial, "No es necesario poner S.A. de C.V."]
              )}
            />
          </FormItem>
        </FormCol>
        <FormCol>
          <FormItem<DistributionPointRequestResource>
            label="Nombre del punto de distribución"
            name="subsidiary"
            rules={Validations.SubsidiaryRules}
          >
            <Input
              className="common-input"
              placeholder="Nombre del punto de distribución"
              addonAfter={Validations.ExtraInfoBtn(
                "Introduce el nombre del punto de distribución",
                [tooltipList.nombrePDD]
              )}
            />
          </FormItem>
        </FormCol>
        <FormCol size="lg">
          <FormItem<DistributionPointRequestResource>
            label="Actividad reportada ante la autoridad fiscal"
            name="companyActivityType"
            rules={Validations.CompanyActivityTypeRules}
          >
            <Select
              placeholder="Tipo de actividad"
              showSearch
              filterOption={Validations.FilterSelectOpts}
              suffixIcon={Validations.ExtraInfoBtn(
                "Selecciona la actividad reportada ante la autoridad fiscal",
                [tooltipList.actividadReportada]
              )}
            >
              {catalogActivityTypes.map((item) => {
                return (
                  <Select.Option key={item.code} value={item.code}>
                    {""
                      .concat(item.code ?? "")
                      .concat(" - ")
                      .concat(item.description ?? "")}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItem>
        </FormCol>
        <FormCol size="lg">
          <FormItem<DistributionPointRequestResource>
            label="Codigo de clave de instalación"
            name="installationKey"
            rules={Validations.InstallationKeyRules}
          >
            <Select
              showSearch
              filterOption={Validations.FilterSelectOpts}
              suffixIcon={Validations.ExtraInfoBtn(
                "Selecciona el codigo de clave de instalación",
                [tooltipList.codigoClaveInstalacion]
              )}
            >
              {installationKeys.map((item) => {
                return (
                  <Select.Option key={item.id} value={item.code}>
                    {item.description}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItem>
        </FormCol>

        <FormCol>
          <FormItem<DistributionPointRequestResource>
            label="Número de clave de instalación"
            name="installationKeyNumber"
            rules={Validations.InstallationKeyNumerRules}
          >
            <Input
              addonBefore={installationKey}
              className="common-input"
              maxLength={4}
              addonAfter={Validations.ExtraInfoBtn(
                "Introduce el número de clave de instalación",
                [tooltipList.numeroClaveInstalacion]
              )}
            />
          </FormItem>
        </FormCol>

        <FormCol>
          <FormItem<DistributionPointRequestResource>
            label="Número de oficio"
            name="operationLicense"
            rules={Validations.OperationLicenseRules}
          >
            <Input
              className="common-input"
              placeholder="Número de oficio del aviso de inicio de operaciones"
              addonAfter={Validations.ExtraInfoBtn(
                "Introduce el número de oficio del aviso de inicio de operaciones",
                [tooltipList.numeroOficio]
              )}
            />
          </FormItem>
        </FormCol>
        <FormCol>
          <FormItem<DistributionPointRequestResource>
            label="Zona horaria"
            rules={Validations.TimeZoneRules}
            name="timeZone"
          >
            <Select
              placeholder="Zona horaria"
              showSearch
              filterOption={Validations.FilterSelectOpts}
              suffixIcon={Validations.ExtraInfoBtn(
                "Selecciona una zona horaria",
                [tooltipList.zonaHoraria]
              )}
            >
              {timeZones.map((item) => {
                return (
                  <Select.Option key={item.value} value={item.value}>
                    {item.text}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItem>
        </FormCol>

        <FormCol size="lg">
          <FormItem<DistributionPointRequestResource>
            label="Número de permiso"
            name="senerLicense"
            rules={Validations.SenerLicenseRules}
          >
            <Input
              className="common-input"
              placeholder="Número de permiso otorgado por la CRE o la Secretaría de Energía"
              addonAfter={Validations.ExtraInfoBtn(
                "Introduce el número de permiso otorgado por la CRE o la Secretaría de Energía",
                [tooltipList.numeroPermiso]
              )}
            />
          </FormItem>
        </FormCol>

        <FormCol size="lg">
          <FormItem<DistributionPointRequestResource>
            label="Descripción"
            name="description"
            rules={Validations.DescriptionRules}
            extra={tooltipList.descripcion}
          >
            <Input.TextArea
              className="common-input"
              placeholder={tooltipList.descripcion}
              rows={4}
            />
          </FormItem>
        </FormCol>
      </Row>
      {isRFCMoral && (
        <>
          <Divider
            orientation="left"
            style={{ fontSize: "15px", color: "#606676" }}
          >
            Datos del representante legal
          </Divider>
          <Row gutter={24}>
            <FormCol size="lg">
              <FormItem<DistributionPointRequestResource>
                name="legalRepresentativeRfc"
                label="RFC"
                rules={Validations.RFCPersonaFisicaRules}
                normalize={(value) => (value || "").toUpperCase()}
              >
                <Input
                  className="common-input"
                  placeholder="RFC del representante legal"
                  addonAfter={Validations.ExtraInfoBtn(
                    "Introduce el RFC del representante legal",
                    [tooltipList.representanteLegal.RFC]
                  )}
                  autoComplete="off"
                />
              </FormItem>
            </FormCol>
            <FormCol size="lg">
              <FormItem<DistributionPointRequestResource>
                name="legalRepresentative"
                label="Nombre del representante legal"
                rules={Validations.NameRepresentanteLegatRules}
                normalize={(value) => (value || "").toUpperCase()}
              >
                <Input
                  className="common-input"
                  placeholder="Nombre o razón social del repredatasetDatesentante legal"
                  addonAfter={Validations.ExtraInfoBtn(
                    "Introduce el Nombre del representante legal",
                    [tooltipList.representanteLegal.nombre]
                  )}
                />
              </FormItem>
            </FormCol>
          </Row>
        </>
      )}
    </div>
  );
}

export default GeneralDataForm;
